import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Card, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Controller } from 'react-hook-form';
import { RHFMultiSelect, RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import RHFRadioGroupLegacy from 'src/components/hook-form/rhf-radio-group-legacy';
import RHFTextArea from 'src/components/hook-form/rhf-text-area';
import { RHFUploadSingleFileAccelerator } from 'src/components/hook-form/rhf-upload';
import { useMyDataStore } from 'src/store/me/me.store';

type Props = {
  hook: any;
};

export default function MissionForm({ hook }: Props) {

  const permissions = useMyDataStore((state) => state.me?.cnAdminInfo?.permissions?.missions)

  return (
    <FormProvider methods={hook.methods} onSubmit={hook.onSubmit}>
      <Card>
        <Stack spacing={2} p={4}>
          <RHFTextField name="name" label="Nome da missão" />
          <RHFTextArea name="description" label="Descrição da missão" />
          <Stack>
            <Stack
              direction="row"
              width="100%"
              sx={{
                alignItems: 'center',
              }}
              spacing={4}
            >
              <Controller
                name="startDate"
                control={hook.control}
                render={({ field }) => (
                  <DatePicker
                    sx={{
                      flex: 1,
                    }}
                    closeOnSelect
                    label="Data de Início"
                    slotProps={{
                      day: {
                        sx: {
                          '&.MuiPickersDay-root.Mui-selected': {
                            backgroundColor: 'primary.main',
                          },
                        },
                      },
                    }}
                    {...field}
                  />
                )}
              />
              <Controller
                name="endDate"
                control={hook.control}
                render={({ field }) => (
                  <DatePicker
                    sx={{
                      flex: 1,
                    }}
                    closeOnSelect
                    label="Data de Fim"
                    slotProps={{
                      day: {
                        sx: {
                          '&.MuiPickersDay-root.Mui-selected': {
                            backgroundColor: 'primary.main',
                          },
                        },
                      },
                    }}
                    {...field}
                  />
                )}
              />
            </Stack>
            {!hook.isValidEndDate && (
              <Typography
                sx={{
                  pl: 1.8,
                  pt: 1,
                }}
                variant="caption"
                color="error"
              >
                Data de término deve ser maior ou igual do que a data de início
              </Typography>
            )}
          </Stack>
          <Stack>
            <Typography>Status:</Typography>
            <RHFRadioGroupLegacy
              name="status"
              options={hook.STATUS_OPTIONS}
              getOptionLabel={hook.STATUS_LABEL}
            />
          </Stack>
          <Stack>
            <Typography>Exibir para as tiers:</Typography>
            <RHFMultiSelect name="visibleForTiers" options={hook.TIERS_OPTIONS} chip checkbox />
          </Stack>
          <RHFTextField name="priority" label="Nível de prioridade" type="number" />
          <Stack>
            <Typography>Tipo de missão</Typography>
            <RHFRadioGroupLegacy
              name="missionType"
              options={hook.TYPE_OPTIONS}
              getOptionLabel={hook.TYPE_LABEL}
            />
          </Stack>
          {hook.type === 'check-in' && (
            <Stack spacing={2}>
              <RHFTextField
                name="checkInData.daysInterval"
                label="Intervalo de dias"
                type="number"
              />

              <RHFTextField name="checkInData.detailsTitle" label="Título do detalhe" />
              <RHFTextField
                name="checkInData.secondaryDetailsTitle"
                label="Título secundário do detalhe"
              />
              <RHFTextField
                name="checkInData.detailsDescriptionTitle"
                label="Título da descrição do detalhe"
              />
              <RHFTextArea name="checkInData.detailsDescription" label="Descrição do detalhe" />

              {hook.checkInFields.map((field: any, index: any) => (
                <Stack
                  key={field.id}
                  spacing={2}
                  sx={{ border: '1px solid #ddd', p: 2, borderRadius: 2 }}
                >
                  <RHFTextField
                    name={`checkInData.stagesReward.${index}`}
                    label={`Recompensa do estágio ${index + 1}`}
                    type="number"
                  />

                  {hook.checkInFields.length > 1 && (
                    <Button color="error" onClick={() => hook.removeCheckIn(index)}>
                      Remover Estágio
                    </Button>
                  )}
                </Stack>
              ))}
              <Stack direction="row" justifyContent="flex-start">
                <Button variant="soft" onClick={() => hook.appendCheckIn(0)}>
                  Adicionar Estágio de Recompensa
                </Button>
              </Stack>
            </Stack>
          )}
          {hook.type === 'quiz' && (
            <Stack spacing={2}>
              <RHFTextField
                name="quizData.chancesPerUser"
                label="Chances por usuário"
                type="number"
                disabled
              />
              <RHFTextField
                name="quizData.pointsPerQuestion"
                label="Pontos por questão"
                type="number"
              />
              <RHFTextField
                name="quizData.timeToFinish"
                label="Tempo para terminar (em segundos)"
                type="number"
              />
              <RHFTextField name="quizData.detailsDescriptionTitle" label="Título do detalhe" />
              <RHFTextArea name="quizData.detailsDescription" label="Detalhe da descrição" />

              {hook.quizFields.map((field: any, index: any) => (
                <Stack
                  key={field.id}
                  spacing={2}
                  sx={{ border: '1px solid #ddd', p: 2, borderRadius: 2 }}
                >
                  <RHFTextField name={`quizData.questions.${index}.question`} label="Pergunta" />

                  <Typography variant="subtitle1">Respostas</Typography>
                  {[0, 1, 2].map((answerIndex) => (
                    <RHFTextField
                      key={answerIndex}
                      name={`quizData.questions.${index}.answers.${answerIndex}`}
                      label={`Resposta ${answerIndex + 1}`}
                    />
                  ))}

                  <Typography>Resposta correta: </Typography>
                  <RHFRadioGroupLegacy
                    name={`quizData.questions.${index}.correctAnswer`}
                    options={hook.CORRECT_ANSWER_OPTIONS}
                    getOptionLabel={hook.CORRECT_ANSWER_LABEL}
                  />

                  {hook.quizFields.length > 1 && (
                    <Button color="error" onClick={() => hook.removeQuiz(index)}>
                      Remover Pergunta
                    </Button>
                  )}
                </Stack>
              ))}
              <Stack direction="row" justifyContent="flex-start">
                <Button
                  variant="soft"
                  onClick={() =>
                    hook.appendQuiz({
                      question: '',
                      answers: ['', '', ''],
                      correctAnswer: '',
                    })
                  }
                >
                  Adicionar Questão
                </Button>
              </Stack>
            </Stack>
          )}
          {hook.type === 'scan-count' && (
            <Stack spacing={2}>
              <RHFTextField
                name="scanCountData.requiredScans"
                label="Quantidade de scans"
                type="number"
              />
              <RHFTextField
                name="scanCountData.reward"
                label="Recompensa (em pontos)"
                type="number"
              />
              <RHFTextField
                name="scanCountData.detailsDescriptionTitle"
                label="Título do detalhe da missão"
              />
              <RHFTextArea
                name="scanCountData.detailsDescription"
                label="Descrição do detalhe da missão"
              />
            </Stack>
          )}
          {hook.type === 'instant' && (
            <Stack spacing={2}>
              <Stack>
                <Typography>Imagem:</Typography>
                <RHFUploadSingleFileAccelerator
                  name="instantData.imageResult"
                  acceptAll={false}
                  // @ts-ignore
                  onDrop={hook.handleDrop}
                />
              </Stack>
              <RHFTextField
                name="instantData.detailsDescriptionTitle"
                label="Título do detalhe da missão"
              />
              <RHFTextArea
                name="instantData.detailsDescription"
                label="Descrição do detalhe da missão"
              />
              <RHFTextField
                name="instantData.reward"
                label="Recompensa (em pontos)"
                type="number"
              />
            </Stack>
          )}
          <RHFTextField
            name="pointsExpiration"
            label="Quantidade de dias para expiração dos pontos"
            type="number"
          />
          <Stack>
            <Typography>Pontos elegíveis para subida de tier:</Typography>
            <RHFRadioGroupLegacy
              name="elegiblePoints"
              options={hook.ELEGIBLE_OPTIONS}
              getOptionLabel={hook.ELEGIBLE_LABEL}
            />
          </Stack>
          <Typography>Regras de exibição:</Typography>
          {hook.rulesFields.map((field: any, index: any) => (
            <Stack
              key={field.id}
              spacing={2}
              sx={{ border: '1px solid #ddd', p: 2, borderRadius: 2 }}
            >
              <Typography>Tipo da regra: </Typography>
              <RHFRadioGroupLegacy
                name={`rules.${index}.ruleType`}
                options={hook.INSTANT_TYPE_OPTIONS}
                getOptionLabel={hook.INSTANT_TYPE_LABEL}
              />

              <Stack>
                <Stack
                  direction="row"
                  width="100%"
                  sx={{
                    alignItems: 'center',
                  }}
                  spacing={4}
                >
                  <Controller
                    name={`rules.${index}.startDate`}
                    control={hook.control}
                    render={({ field: fieldC, fieldState: { error } }) => (
                      <DatePicker
                        sx={{
                          flex: 1,
                        }}
                        closeOnSelect
                        label="Data de Início"
                        slotProps={{
                          day: {
                            sx: {
                              '&.MuiPickersDay-root.Mui-selected': {
                                backgroundColor: 'primary.main',
                              },
                            },
                          },
                          textField: {
                            error: !!error,
                            helperText: error ? error?.message : ''
                          }
                        }}
                        {...fieldC}
                      />
                    )}
                  />
                  <Controller
                    name={`rules.${index}.endDate`}
                    control={hook.control}
                    render={({ field: fieldC, fieldState: { error } }) => (
                      <DatePicker
                        sx={{
                          flex: 1,
                        }}
                        closeOnSelect
                        label="Data de Fim"
                        slotProps={{
                          day: {
                            sx: {
                              '&.MuiPickersDay-root.Mui-selected': {
                                backgroundColor: 'primary.main',
                              },
                            },
                          },
                          textField: {
                            error: !!error,
                            helperText: error ? error?.message : ''
                          }
                        }}
                        {...fieldC}
                      />
                    )}
                  />
                </Stack>
              </Stack>

              <Button color="error" onClick={() => hook.removeRules(index)}>
                Remover Regra
              </Button>
            </Stack>
          ))}
          <Stack direction="row" justifyContent="flex-start">
            <Button
              variant="soft"
              onClick={() =>
                hook.appendRules({
                  ruleType: 'nfScan',
                  startDate: new Date(),
                  endDate: new Date()
                })
              }
            >
              Adicionar Regra
            </Button>
          </Stack>
          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton type="submit" variant="contained" loading={hook.isSubmitting} disabled={(hook.isEdit && !permissions?.update) || (!hook.isEdit && !permissions?.create)}>
              {!hook.isEdit ? 'Cadastrar Missão' : 'Editar Missão'}
            </LoadingButton>
          </Stack>
        </Stack>
      </Card>
    </FormProvider>
  );
}
